import { useSearchParams } from 'react-router';
import type { LocalAddress, LocalContact } from '@/context/create-account-flow';

interface Step {
  id: string;
  title: string;
  href: string;
  completed?: boolean;
  disabled?: boolean;
  isActive?: boolean;
}

const rawSteps = (): Step[] => [
  {
    id: 'account',
    title: 'CONTA',
    href: '/commercial/accounts/create?step=account',
  },
  {
    id: 'addresses',
    title: 'ENDERECOS',
    href: '/commercial/accounts/create?step=addresses',
  },
  {
    id: 'contacts',
    title: 'CONTATOS',
    href: '/commercial/accounts/create?step=contacts',
  },
];

export function useAccountStepProgress({
  accountId,
  addresses,
  contacts,
}: {
  accountId: string;
  addresses: LocalAddress[];
  contacts: LocalContact[];
}) {
  const [searchParams] = useSearchParams();
  const currentStep = searchParams.get('step') || 'account';

  const steps = rawSteps();

  const getCurrentStepIndex = () => {
    if (currentStep === 'addresses') return 1;
    if (currentStep === 'contacts') return 2;
    return 0;
  };

  const currentIndex = getCurrentStepIndex();

  // Pre-compute completion status for ordering gates
  const completions = [!!accountId, addresses.length > 0, contacts.length > 0];

  return steps.map((step, index) => {
    const isActive = index === currentIndex;
    const isCompleted = index < currentIndex || completions[index];
    const isFutureStep = !isActive && !isCompleted;

    // Content-based gate
    const contentDisabled =
      (step.id === 'addresses' && !accountId) ||
      (step.id === 'contacts' && (!accountId || addresses.length === 0));

    // Step ordering gate: previous step must be completed
    const orderingDisabled = index > 0 && !completions[index - 1];

    const disabled = isFutureStep && (contentDisabled || orderingDisabled);

    return {
      ...step,
      isActive,
      disabled,
      completed: isCompleted,
      href: disabled ? '#' : step.href,
    };
  });
}
